'use client';

import { MinusIcon } from '@app/app/_icons/MinusIcon';
import { PlusIcon } from '@app/app/_icons/PlusIcon';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';

const supportedFontSizes = ['normal', 'large', 'extra-large', 'huge'];

export function FontSizeConfigurator() {
    const t = useTranslations();

    const [currentFontSize, setCurrentFontSize] = useState<number>(() => {
        const storedFontSize = supportedFontSizes.indexOf(localStorage.getItem('font-size') ?? '');

        return storedFontSize === -1 ? 0 : storedFontSize;
    });

    const handleIncreaseClick = useCallback(() => {
        setCurrentFontSize(prevFontSize => {
            if (prevFontSize >= supportedFontSizes.length - 1) {
                return supportedFontSizes.length - 1;
            }

            return prevFontSize + 1;
        });
    }, []);

    const handleDecreaseClick = useCallback(() => {
        setCurrentFontSize(prevFontSize => {
            if (prevFontSize <= 0) {
                return 0;
            }

            return prevFontSize - 1;
        });
    }, []);

    useChangeEffect(() => {
        document.body.dataset.accessibilityFontSize = supportedFontSizes[currentFontSize];
        localStorage.setItem('font-size', supportedFontSizes[currentFontSize]);
    }, currentFontSize);

    return (
        <div className="flex items-center justify-between gap-10">
            <div className="grow">{t('accessibility.fontSize')}</div>
            <div className="flex items-stretch">
                <button
                    type="button"
                    className="rounded-l-md border border-neutral-300 px-3 py-3"
                    onClick={handleDecreaseClick}>
                    <MinusIcon className="size-4" />
                </button>
                <div className="flex items-center border-y border-neutral-300 px-4 text-neutral-500">
                    {t(`accessibility.fontSizes.${supportedFontSizes[currentFontSize]}`)}
                </div>
                <button
                    type="button"
                    className="rounded-r-md border border-neutral-300 px-3 py-3"
                    onClick={handleIncreaseClick}>
                    <PlusIcon className="size-4" />
                </button>
            </div>
        </div>
    );
}
